// Checklist: studioaeye-backend/docs/tests-Backend/testingCheckList.md
// Features: M004-F0607, M004-F0609
// Route wrapper for /app/admin/rooms/access/advanced (design doc M004-D013 §10.6).
// Resolves organisation scope using the same shared, already-tested helpers as
// AdminRooms (adminRoomsOrganisationScope.js), then mounts the existing,
// UNMODIFIED AccessControlPanel — the only surface that can create areas,
// door bindings, schedule groups, and priority/auto-matching policy.
import React, { useContext, useEffect, useState } from 'react';
import AuthContext from '../../context/AuthContext';
import { organisationApi } from '../../services/api';
import AccessControlPanel from './AccessControlPanel';
import {
  canLoadAdminRoomsScope,
  isGlobalSuperAdmin,
  resolveAdminRoomsSelection,
  selectAdminRoomsOrganisations,
} from './adminRoomsOrganisationScope';

const AccessControlPanelAdvanced = () => {
  const { user } = useContext(AuthContext);
  const authScopeSubject = user?.uuid ?? user?.id ?? user?.email ?? '';
  const canResolveScope = Boolean(authScopeSubject)
    && (user?.role === 'admin' || user?.role === 'super_admin');
  const authScopeIdentity = [authScopeSubject || 'anonymous', user?.role ?? 'none'].map(String).join(':');
  const [organisations, setOrganisations] = useState([]);
  const [selectedOrgFilter, setSelectedOrgFilter] = useState('');
  const [scopeReady, setScopeReady] = useState(false);
  const [scopeSucceeded, setScopeSucceeded] = useState(false);

  useEffect(() => {
    let active = true;
    const requestedScopeUser = { role: user?.role };
    if (!canResolveScope) {
      setOrganisations([]);
      setSelectedOrgFilter('');
      setScopeReady(true);
      setScopeSucceeded(false);
      return undefined;
    }
    setScopeReady(false);
    organisationApi.getMine().then((response) => {
      if (!active) return;
      const orgsData = response.data?.data || [];
      const nextOrganisations = selectAdminRoomsOrganisations(orgsData, requestedScopeUser);
      setOrganisations(nextOrganisations);
      setSelectedOrgFilter((current) => resolveAdminRoomsSelection(current, nextOrganisations, requestedScopeUser));
      setScopeSucceeded(true);
    }).catch((err) => {
      console.error('Failed to resolve Access Advanced organisation scope:', err);
      if (!active) return;
      setOrganisations([]);
      setSelectedOrgFilter('');
      setScopeSucceeded(false);
    }).finally(() => {
      if (active) setScopeReady(true);
    });
    return () => { active = false; };
  }, [authScopeIdentity, canResolveScope, user?.role]);

  const canLoad = canLoadAdminRoomsScope(scopeReady && scopeSucceeded, selectedOrgFilter, user);

  return (
    <section className="access-control-panel-advanced-page">
      <header className="access-panel-header">
        <h2>Advanced access configuration</h2>
        {organisations.length > 0 && (
          <select
            className="form-input"
            value={selectedOrgFilter}
            onChange={(event) => setSelectedOrgFilter(resolveAdminRoomsSelection(event.target.value, organisations, user))}
            aria-label="Organisation"
            data-testid="access-advanced-organisation-filter"
          >
            {isGlobalSuperAdmin(user) && <option value="">All Organisations</option>}
            {organisations.map((organisation) => (
              <option key={organisation.id} value={organisation.id}>{organisation.name}</option>
            ))}
          </select>
        )}
      </header>
      {scopeReady && !canLoad && (
        <p className="form-error" role="alert" data-testid="access-advanced-organisation-scope-empty">
          No administered organisation is available for Access configuration.
        </p>
      )}
      {canLoad && (
        <AccessControlPanel
          organisationId={selectedOrgFilter}
          organisation={organisations.find((org) => String(org.id) === String(selectedOrgFilter)) || null}
        />
      )}
    </section>
  );
};

export default AccessControlPanelAdvanced;
